/**
 * Meta standard events use `fbq("track", …)`; any other (custom) event name
 * must use `fbq("trackCustom", …)`, otherwise Meta drops/misclassifies it.
 * @see https://developers.facebook.com/docs/meta-pixel/reference
 */
const STANDARD_EVENTS = new Set([
  "AddPaymentInfo",
  "AddToCart",
  "AddToWishlist",
  "CompleteRegistration",
  "Contact",
  "CustomizeProduct",
  "Donate",
  "FindLocation",
  "InitiateCheckout",
  "Lead",
  "PageView",
  "Purchase",
  "Schedule",
  "Search",
  "StartTrial",
  "SubmitApplication",
  "Subscribe",
  "ViewContent"
]);

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * Fire a Meta (Facebook) Pixel event via the global `fbq`.
 *
 * `fbq` is loaded by the base pixel snippet in `app/(default)/head.tsx`
 * (App Router) and by `react-facebook-pixel` in `pages/_app.tsx` (Pages
 * Router), so it is available in both halves of the hybrid app once the pixel
 * has initialised.
 *
 * Only fires in production builds, so development and test (incl. E2E)
 * traffic stays out of Meta. Standard events go through `track`, custom event
 * names through `trackCustom`.
 */
export function trackPixel(event: string, params?: Record<string, unknown>) {
  if (
    process.env.NODE_ENV !== "production" ||
    typeof window === "undefined" ||
    typeof window.fbq !== "function"
  ) {
    return;
  }

  const method = STANDARD_EVENTS.has(event) ? "track" : "trackCustom";

  if (params) {
    window.fbq(method, event, params);
  } else {
    window.fbq(method, event);
  }
}
